import {
  CandidateType,
  ControllerPlacementPass,
  InsertionWindingDirection,
} from "building-types/electrical/candidateTypesEnum";
import type { ElectricalLayoutType } from "building-types/electrical/electricalLayoutTypesEnum";
import uid from "common/utils/uid";
import type { Vec2 } from "geom/vec2";

interface BaseControllerPlacementCandidate {
  readonly id: string;
  readonly type: CandidateType;
  readonly tags: { [name: string]: boolean };
  readonly levelId: string;
  readonly roomId?: string;
  readonly position: Vec2;
  readonly insertionPass: ControllerPlacementPass;
  readonly insertionDirection: InsertionWindingDirection;
  readonly normal: Vec2;
}

export type ControllerPlacementCandidate =
  | OpeningControllerPlacementCandidate
  | ExplicitControllerPlacementCandidate
  | CornerControllerPlacementCandidate;

interface ExplicitControllerPlacementCandidate extends BaseControllerPlacementCandidate {
  readonly type: CandidateType.EXPLICIT;
}

export interface OpeningControllerPlacementCandidate extends BaseControllerPlacementCandidate {
  readonly type: CandidateType.OPENING;
  readonly sourceId: string; // doorId
  readonly toRoomId: string | undefined;
}

export interface CornerControllerPlacementCandidate extends BaseControllerPlacementCandidate {
  readonly type: CandidateType.CORNER;
}

interface ExplicitCandidatesNewInfo {
  tag: string | undefined;
  levelId: string;
  roomId: string;
  x: number;
  y: number;
  nX: number;
  nY: number;
}

export function explicitCandidateNew({
  tag,
  levelId,
  roomId,
  x,
  y,
  nX,
  nY,
}: ExplicitCandidatesNewInfo): ExplicitControllerPlacementCandidate {
  return {
    id: uid(),
    type: CandidateType.EXPLICIT,
    levelId,
    roomId,
    position: [x, y],
    insertionPass: ControllerPlacementPass.EXPLICIT,
    insertionDirection: InsertionWindingDirection.CENTER,
    normal: [nX, nY],
    tags: newTagTable(tag),
  };
}

function newTagTable(firstEntry: string | undefined) {
  const tagTable: { [name: string]: boolean } = {};
  if (firstEntry !== undefined) {
    tagTable[firstEntry] = true;
  }
  return tagTable;
}

interface CornerCandidateNewInfo {
  readonly levelId: string;
  readonly roomId?: string;
  readonly insertionDirection: InsertionWindingDirection;
  readonly x: number;
  readonly y: number;
  readonly nX: number;
  readonly nY: number;
}

export function cornerCandidateNew({
  levelId,
  roomId,
  insertionDirection,
  x,
  y,
  nX,
  nY,
}: CornerCandidateNewInfo): CornerControllerPlacementCandidate {
  return {
    id: uid(),
    type: CandidateType.CORNER,
    levelId,
    roomId,
    position: [x, y],
    insertionPass: ControllerPlacementPass.BY_CORNER,
    insertionDirection,
    normal: [nX, nY],
    tags: {},
  };
}

export const DefaultPassRankingTable: {
  [key in ControllerPlacementPass]: number;
} = {
  [ControllerPlacementPass.EXPLICIT]: 1,
  [ControllerPlacementPass.BY_DOOR_LATCH_SIDE]: 2,
  [ControllerPlacementPass.BY_DOOR]: 3,
  [ControllerPlacementPass.BY_DOOR_BEHIND_SWING]: 4,
  [ControllerPlacementPass.BY_CORNER]: 5,
};

export type ControllerPlacementCandidateTable = { [roomSetIndex: number]: ControllerPlacementCandidateInfo[] };

export interface PositionAtControllerPlacement {
  readonly loadApproximatePosition: Vec2 | undefined;
  readonly subControllerPosition: Vec2;
  readonly electricalLayoutType: ElectricalLayoutType;
}

export interface ControllerPlacementCandidateInfo {
  readonly candidate: ControllerPlacementCandidate;
  readonly subControllerPositions: PositionAtControllerPlacement[];
}

export function controllerPlacementCandidateInfoNew(
  candidate: ControllerPlacementCandidate,
): ControllerPlacementCandidateInfo {
  return { candidate, subControllerPositions: [] };
}
